import React from 'react';
import HBText from '../HBText/HBText';
import styled from 'styled-components';
import type { HBTableFiltersProps } from './HBTable.types';
import { vars } from '../../theme';
import HBInput from '../HBInput/HBInput';
import HBLinkButton from '../HBLinkButton/HBLinkButton';
import HBTag from '../HBTag/HBTag';

// if/when we want to add a side panel for extra filters.
// const MAX_FILTERS_TO_SHOW = 4;

function HBTableFilters<T>({ table }: HBTableFiltersProps<T>) {
    const {
        isLoading,
        search,
        setSearch,
        filterElements,
        allFilterLabels,
        clearAllFilters,
        itemLabel,
        itemsLabel
    } = table.options.meta;

    // const hasMoreThanMax = filterElements?.length > MAX_FILTERS_TO_SHOW;
    // const filtersToShow = hasMoreThanMax
    //     ? filterElements?.slice(0, MAX_FILTERS_TO_SHOW - 1)
    //     : filterElements;

    const rowCount = table.getRowCount();
    const singularLabel = itemLabel ?? 'item';
    const pluralLabel = itemsLabel ?? (itemLabel ? `${itemLabel}s` : 'items');
    const rowCountLabel = rowCount === 1 ? singularLabel : pluralLabel;

    const isCountBesideSearch = !filterElements && search != null;
    const totalCount = (
        <HBText
            type="body14"
            color="text.secondary"
            ellipsis
            testId={`${table.options.meta.testIdPrefix}-total-count`}
        >
            {isLoading ? 'Loading...' : `${rowCount} ${rowCountLabel}`}
        </HBText>
    );

    return (
        <Container>
            {(filterElements || search != null) && (
                <TopContainer
                    $isCentered={isCountBesideSearch}
                    data-testid={`${table.options.meta.testIdPrefix}-top-row`}
                >
                    <TopLeftSideContainer>
                        {filterElements}
                        {isCountBesideSearch && totalCount}
                        {/* {hasMoreThanMax && (
                        <HBLinkButton
                            size="sm"
                            onClick={() => {}}
                            linkButtonTheme="solid"
                            testIdName="show-all-filters"
                            icon={<Filter16 />}
                        >
                            More filters
                        </HBLinkButton>
                    )} */}
                    </TopLeftSideContainer>

                    <TopRightSideContainer>
                        {search != null && (
                            <HBInput
                                size="sm"
                                placeholder="Search"
                                testIdName="search"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        )}
                    </TopRightSideContainer>
                </TopContainer>
            )}
            {allFilterLabels?.length > 0 && (
                <AllFilterTagsContainer>
                    <HBText type="body14" color="text.secondary">
                        Filtered by
                    </HBText>
                    {allFilterLabels.map(filter => (
                        <HBTag
                            key={filter.label}
                            label={filter.label}
                            size="sm"
                            color={filter.color ?? 'neutral'}
                            onDelete={filter.onRemove}
                        />
                    ))}
                    <HBLinkButton
                        size="sm"
                        onClick={clearAllFilters}
                        linkButtonTheme="solid"
                        testIdName="clear-all-filters"
                    >
                        Clear all
                    </HBLinkButton>
                </AllFilterTagsContainer>
            )}
            {!isCountBesideSearch && totalCount}
        </Container>
    );
}

export default HBTableFilters;

// -------------------------- Styles -------------------------- //

const Container = styled.div`
    padding: 16px;
    border-bottom: 1px solid ${vars.colors.stroke.secondary};
    display: flex;
    flex-direction: column;
    gap: 16px;
    background-color: ${vars.colors.surface.container};
    border-top-right-radius: inherit;
    border-top-left-radius: inherit;
`;

const TopContainer = styled.div<{ $isCentered: boolean }>`
    display: flex;
    align-items: ${({ $isCentered }) =>
        $isCentered ? 'center' : 'flex-start'};
    justify-content: space-between;
    gap: 16px;
`;

const TopLeftSideContainer = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    flex-grow: 1;
    min-width: 0;
    gap: 8px 12px;
    > * {
        max-width: 200px;
    }
`;

const TopRightSideContainer = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
`;

const AllFilterTagsContainer = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
`;
